import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography, 
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {Close, Delete, Add, Person} from '@mui/icons-material';
import {useSources} from "../../context/SourcesContext";
import {ProjectUser} from "../../types";
import {useDialog} from "../../context/DialogContext";
import {useAuth} from "../../context/AuthContext";

interface UserSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const UserSettingsDialog: React.FC<UserSettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const { user: currentUser } = useAuth();
  const { 
    projectUsers, 
    loadProjectUsers, 
    addUserToProject, 
    removeUserFromProject, 
    updateUserProjectRole,
    selectedProject,
    canManageProject,
    updateProjectGPTModel
  } = useSources();

  const { openDiscardDataDialog } = useDialog()
  
  const [newUserEmail, setNewUserEmail] = useState('');
  const [selectedGPTModel, setSelectedGPTModel] = useState<string>('5');

  // Update local state when project GPT model changes
  useEffect(() => {
    if (selectedProject?.gptModel) {
      setSelectedGPTModel(selectedProject.gptModel);
    }
  }, [selectedProject?.gptModel]);

  const handleAddUser = () => {
    if (newUserEmail.trim()) {
      addUserToProject(newUserEmail.trim());
      setNewUserEmail('');
    }
  };

  const handleRoleChange = (userId: string, currentRole: 'VIEWER' | 'OWNER') => {
    const newRole = currentRole === 'OWNER' ? 'VIEWER' : 'OWNER';
    updateUserProjectRole(userId, newRole);
  };

  const handleRemoveUser = (userId: string) => {
    removeUserFromProject(userId);
  };

  const handleGPTModelChange = (event: any) => {
    const newModel = event.target.value;
    setSelectedGPTModel(newModel);
    updateProjectGPTModel(newModel);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Settings</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* AI Model Selection Section */}
        <Box sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 2 }}>
            AI Model
          </Typography>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="gpt-model-select-label">Select Model</InputLabel>
            <Select
              labelId="gpt-model-select-label"
              value={selectedGPTModel}
              label="Select Model"
              onChange={handleGPTModelChange}
              disabled={!canManageProject()}
            >
              <MenuItem value="5">GPT-5.1 (Latest)</MenuItem>
              <MenuItem value="4o">GPT-4o (Faster)</MenuItem>
            </Select>
          </FormControl>
          
          {!canManageProject() && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Only project owners and admins can change the AI model
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Project Users Management Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Project Users
          </Typography>
          
          {/* Add User Section */}
          {canManageProject() && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter user email"
                size="small"
                sx={{ flexGrow: 1 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddUser();
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddUser}
                disabled={!newUserEmail.trim()}
              >
                Add User
              </Button>
            </Box>
          )}

          {/* Users Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Is Owner</TableCell>
                  {canManageProject() && <TableCell align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(projectUsers || []).map((user: ProjectUser) => {
                  const isCurrentUser = currentUser?.id === user.id;
                  
                  return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={user.avatar}
                          sx={{ width: 24, height: 24 }}
                        >
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {user.name || ''}
                          </Typography>
                          {user.isAdmin && (
                            <Chip 
                              label="Admin" 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: '16px' }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={isCurrentUser ? "You cannot change your own role" : ""}>
                        <span>
                          <Checkbox
                            checked={user.role === 'OWNER'}
                            onChange={() => handleRoleChange(user.id, user.role)}
                            disabled={!canManageProject() || isCurrentUser}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    {canManageProject() && (
                      <TableCell align="center">
                        <Tooltip title={isCurrentUser ? "You cannot remove yourself from the project" : ""}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveUser(user.id)}
                              disabled={isCurrentUser}
                              sx={{
                                color: isCurrentUser ? 'text.disabled' : 'error.main',
                                '&:hover': { 
                                  backgroundColor: isCurrentUser ? 'transparent' : 'error.light' 
                                }
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })}
                {projectUsers && projectUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canManageProject() ? 4 : 2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        {canManageProject() && (
          <Button
              variant="outlined"
              startIcon={<Delete />}
              onClick={openDiscardDataDialog}
              size="small"
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  color: 'error.dark',
                  backgroundColor: 'error.light'
                }
              }}
          >
           Discard all monitoring data
          </Button>
        )}

        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSettingsDialog;